import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FleetTabs, PageHeader, NewCampaignModal, Pill, StatusPill, ActivityPill, ago, errText, useLiveRefresh } from './common';

function Tile({ label, value, color, to }) {
  const body = (
    <div className="card" style={{ padding: '12px 16px', minWidth: 140 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || 'inherit' }}>{value}</div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>{body}</Link> : body;
}

// Attack signs colour the whole row, so they stand out without opening the server.
function rowAlert(activity) {
  const c = activity === 'kernel_errors_seen' ? 'var(--danger)' : activity === 'ip_options_traffic_seen' ? 'var(--warning)' : null;
  return c ? { background: `color-mix(in srgb, ${c} 12%, transparent)`, boxShadow: `inset 4px 0 0 ${c}` } : {};
}

const FILTER_KEYS = ['status', 'tag', 'mitigated', 'monitored', 'stale', 'activity', 'disabled', 'token', 'q'];

// Column sort keys. Status, activity, mitigated and monitored sort by how worrying they are, not by name.
const STATUS_RANK = { vulnerable: 0, likely_vulnerable: 1, reboot_required: 2, unknown: 3, no_report: 4, fixed: 5, not_affected: 6 };
const ACTIVITY_RANK = { kernel_errors_seen: 0, ip_options_traffic_seen: 1, crash_report_seen: 2, none: 3 };
const SORTS = {
  server: h => h.cn.toLowerCase(),
  status: h => STATUS_RANK[h.status || 'no_report'] ?? 9,
  mitigated: h => (h.mitigated == null ? 2 : h.mitigated ? 1 : 0),
  monitored: h => (h.monitoring == null ? 2 : h.monitoring ? 1 : 0),
  kernel: h => `${h.kernel || ''} ${h.os || ''}`.toLowerCase(),
  tags: h => h.tags.join(',').toLowerCase(),
  activity: h => ACTIVITY_RANK[h.activity || 'none'] ?? 9,
  running: h => (h.active_job ? `${h.active_job.action} ${h.active_job.state}` : '~'),
  token: h => (h.enroll_token ? (h.enroll_token.label || '').toLowerCase() : '~'),
  last_seen: h => (h.last_seen ? new Date(h.last_seen).getTime() : 0),
};

export default function FleetHosts() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [hosts, setHosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);

  const query = FILTER_KEYS.map(k => params.get(k) || '').join('|');
  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const q = new URLSearchParams();
      FILTER_KEYS.forEach(k => { if (params.get(k)) q.set(k, params.get(k)); });
      const [s, h] = await Promise.all([axios.get('/cve-fleet/summary'), axios.get(`/cve-fleet/hosts?${q}`)]);
      setSummary(s.data); setHosts(h.data.hosts); setTags(h.data.tags); setTokens(h.data.tokens || []); setError('');
    } catch (e) { setError(errText(e, 'Failed to load servers')); }
    finally { if (!silent) setLoading(false); }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => load(true), 60000);

  // The API needs the login token, so fetch the file and hand it to the browser.
  const [exporting, setExporting] = useState(false);
  async function exportCsv() {
    setExporting(true);
    try {
      const r = await axios.get('/cve-fleet/hosts.csv', { responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || 'cve-fleet-servers.csv';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { alert(errText(e, 'Export failed')); }
    finally { setExporting(false); }
  }

  function setFilter(k, v) {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v); else n.delete(k);
    setParams(n); setSelected(new Set());
  }
  function toggle(cn) { const n = new Set(selected); n.has(cn) ? n.delete(cn) : n.add(cn); setSelected(n); }
  const sortKey = params.get('sort') || '';
  const sortDir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  function sortBy(key) {
    const n = new URLSearchParams(params);
    if (sortKey === key && sortDir === 'asc') n.set('dir', 'desc');
    else { n.set('sort', key); n.delete('dir'); }
    setParams(n);
  }
  // No column chosen: keep the server's order (attack signs first, then by name).
  const sorted = !SORTS[sortKey] ? hosts : [...hosts].sort((a, b) => {
    const x = SORTS[sortKey](a), y = SORTS[sortKey](b);
    const c = x < y ? -1 : x > y ? 1 : a.cn.localeCompare(b.cn);
    return sortDir === 'desc' ? -c : c;
  });
  const selectable = hosts.filter(h => h.enrolled && !h.disabled);
  const allOn = selectable.length > 0 && selectable.every(h => selected.has(h.cn));

  const s = summary;
  return (
    <div>
      <PageHeader />
      <div className="page-body">
        <FleetTabs />
        {s && !s.pki_ready && <div className="error-msg">The fleet CA is not initialised on the server yet, so agents cannot enroll. Run scripts/cve-fleet-setup.sh.</div>}
        {s && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Tile label="Servers" value={s.total} to="/cve-fleet" />
            <Tile label="Exposed (vulnerable / likely / reboot)" value={s.exposed} color={s.exposed ? 'var(--danger)' : undefined} to="/cve-fleet?status=exposed" />
            <Tile label="Exposed and not mitigated" value={s.exposed_unmitigated} color={s.exposed_unmitigated ? 'var(--danger)' : undefined} to="/cve-fleet?status=exposed&mitigated=no" />
            <Tile label="Mitigated" value={s.mitigated} to="/cve-fleet?mitigated=yes" />
            <Tile label="Fixed / not affected" value={(s.by_status.fixed || 0) + (s.by_status.not_affected || 0)} color="var(--success)" />
            <Tile label="Attack signs seen" value={s.activity_seen} color={s.activity_seen ? 'var(--warning)' : undefined} to="/cve-fleet?activity=yes" />
            <Tile label={`Not polling (${s.stale_after_minutes || 5} min)`} value={s.stale} color={s.stale ? 'var(--warning)' : undefined} to="/cve-fleet?stale=yes&disabled=no" />
            <Tile label="Campaigns running / paused" value={`${s.campaigns_running} / ${s.campaigns_paused}`} to="/cve-fleet/campaigns" />
            <Tile label="SIEM alerts sent" value={s.siem?.configured ? (s.siem.counts.sent || 0) : 'off'}
                  color={(s.siem?.counts?.failed || s.siem?.counts?.pending) ? 'var(--warning)' : undefined} to="/cve-fleet/audit" />
          </div>
        )}
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
          <input className="form-input" style={{ width: 200 }} placeholder="Search name, OS, kernel" defaultValue={params.get('q') || ''}
                 onKeyDown={e => { if (e.key === 'Enter') setFilter('q', e.target.value.trim()); }} />
          <select className="form-input" style={{ width: 190 }} value={params.get('status') || ''} onChange={e => setFilter('status', e.target.value)}>
            <option value="">All statuses</option>
            <option value="exposed">Exposed (any)</option>
            {['vulnerable', 'likely_vulnerable', 'reboot_required', 'fixed', 'not_affected', 'unknown', 'no_report'].map(x => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
          </select>
          <select className="form-input" style={{ width: 150 }} value={params.get('tag') || ''} onChange={e => setFilter('tag', e.target.value)}>
            <option value="">All tags</option>
            {tags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select className="form-input" style={{ width: 150 }} value={params.get('mitigated') || ''} onChange={e => setFilter('mitigated', e.target.value)}>
            <option value="">Mitigated: any</option><option value="yes">Mitigated</option><option value="no">Not mitigated</option>
          </select>
          <select className="form-input" style={{ width: 160 }} value={params.get('monitored') || ''} onChange={e => setFilter('monitored', e.target.value)}
                  title="Whether the monitor rule was in place at the server's last report">
            <option value="">Monitored: any</option><option value="yes">Monitored</option><option value="no">Not monitored</option>
          </select>
          <select className="form-input" style={{ width: 200 }} value={params.get('token') || ''} onChange={e => setFilter('token', e.target.value)}>
            <option value="">Enrolled with: any</option>
            {tokens.map(t => <option key={t.id} value={t.id}>{t.label} ({t.prefix}…) · {t.hosts}</option>)}
          </select>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            {selected.size ? `Run action on ${selected.size} selected…` : 'New campaign…'}
          </button>
          <button className="btn btn-secondary" style={{ marginRight: 8 }} onClick={exportCsv} disabled={exporting}
                  title="Every server and every column, as of now; filters on this page are not applied">
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button className="btn btn-secondary" onClick={() => load()} disabled={loading}>Refresh</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
        {loading ? <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
          : hosts.length === 0 ? (
            <div className="empty-state"><h3>No servers {params.toString() ? 'match these filters' : 'yet'}</h3>
              <p>Install the agent on a server; it appears here once it enrolls. <Link to="/cve-fleet/enrollment">Enrollment and installer →</Link></p></div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-muted)' }}>
                    <th style={{ padding: '8px 10px' }}><input type="checkbox" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(selectable.map(h => h.cn)))} /></th>
                    {[['server', 'Server'], ['status', 'Status'], ['mitigated', 'Mitigated'], ['monitored', 'Monitored'], ['kernel', 'Kernel · OS'],
                      ['tags', 'Tags'], ['activity', 'Activity'], ['running', 'Running'], ['token', 'Enrolled with'],
                      ['last_seen', 'Last seen']].map(([key, label]) => (
                      <th key={key} style={{ padding: '8px 10px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                          onClick={() => sortBy(key)} title="Sort">
                        {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(h => (
                    <tr key={h.cn} style={{ borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', opacity: h.disabled ? 0.55 : 1, ...rowAlert(h.activity) }}
                        onClick={() => navigate(`/cve-fleet/hosts/${encodeURIComponent(h.cn)}`)}>
                      <td style={{ padding: 10 }} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" disabled={!h.enrolled || h.disabled} checked={selected.has(h.cn)} onChange={() => toggle(h.cn)} />
                      </td>
                      <td style={{ padding: 10 }}><strong>{h.cn}</strong>
                        {h.disabled && <> <Pill label="disabled" color="var(--danger)" /></>}
                        {!h.enrolled && <> <Pill label="awaiting re-enroll" color="var(--warning)" /></>}
                        {h.hostname && h.hostname !== h.cn && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{h.hostname}</div>}
                      </td>
                      <td style={{ padding: 10 }}><StatusPill status={h.status} /></td>
                      <td style={{ padding: 10 }}>{h.mitigated == null ? '—' : h.mitigated ? <Pill label="yes" color="var(--success)" /> : <Pill label="no" color="var(--danger)" />}</td>
                      <td style={{ padding: 10 }}>{h.monitoring == null ? '—' : h.monitoring ? <Pill label="yes" color="var(--success)" /> : <Pill label="no" color="var(--text-muted)" />}</td>
                      <td style={{ padding: 10, fontSize: 12 }}><div>{h.kernel || '—'}</div><div style={{ color: 'var(--text-muted)' }}>{h.os}</div></td>
                      <td style={{ padding: 10, fontSize: 12 }}>{h.tags.join(', ')}</td>
                      <td style={{ padding: 10 }}><ActivityPill activity={h.activity} />
                        {(h.ip_option_packets?.dropped > 0 || h.ip_option_packets?.monitored > 0) &&
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                            {[h.ip_option_packets.dropped && `${h.ip_option_packets.dropped} dropped`,
                              h.ip_option_packets.monitored && `${h.ip_option_packets.monitored} seen by monitor`].filter(Boolean).join(' · ')}
                          </div>}
                      </td>
                      <td style={{ padding: 10 }}>{h.active_job ? <Pill label={`${h.active_job.action} · ${h.active_job.state}`} color="var(--accent)" /> : ''}</td>
                      <td style={{ padding: 10, fontSize: 12 }}>{h.enroll_token
                        ? <><div>{h.enroll_token.label || `token #${h.enroll_token.id}`}</div><code style={{ color: 'var(--text-muted)' }}>{h.enroll_token.prefix}…</code></>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                      <td style={{ padding: 10, whiteSpace: 'nowrap', color: h.stale ? 'var(--warning)' : undefined }}>{ago(h.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      {showNew && (
        <NewCampaignModal preset={selected.size ? { hosts: [...selected] } : null} tags={tags}
                          onClose={() => setShowNew(false)}
                          onCreated={c => navigate(`/cve-fleet/campaigns/${c.id}`)} />
      )}
    </div>
  );
}
